import { localPrefs } from "../../../shared/native-modules/tare/getLocalPrefs";

/**
 * Service for storing user interactions with job listings
 * Uses localPrefs for persistent storage
 */

// Namespace and key definitions
const NAMESPACE = "ViewJob";
const JOBS_WITH_FEEDBACK_KEY = "jobsWithFeedback";

// Feedback types
export enum FeedbackType {
  SAVED = "saved",
  NOT_NOW = "notNow",
}

// Interface for job feedback data
export interface JobFeedbackData {
  type: FeedbackType;
}

// Interface for feedback storage
export interface FeedbackStorage {
  [jobKey: string]: JobFeedbackData;
}

/**
 * Get all jobs with feedback data
 * @returns Object containing job feedback data
 */
async function getJobsWithFeedback(): Promise<FeedbackStorage> {
  const jobsWithFeedbackStr = await localPrefs.getString(NAMESPACE, JOBS_WITH_FEEDBACK_KEY, "{}");
  try {
    return JSON.parse(jobsWithFeedbackStr ?? "{}") as FeedbackStorage;
  } catch (error) {
    console.error("Error parsing jobs with feedback:", error);
    return {};
  }
}

/**
 * Save the feedback data for jobs
 * @param feedbackData Object containing job feedback data
 */
async function saveJobsWithFeedback(feedbackData: FeedbackStorage): Promise<void> {
  await localPrefs.setString(NAMESPACE, JOBS_WITH_FEEDBACK_KEY, JSON.stringify(feedbackData));
}

/**
 * Mark a job as having received feedback so the related prompt won't be shown again
 * @param jobKey Job ID
 * @param feedbackType Type of feedback (saved or not interested)
 */
export async function markJobWithFeedback(
  jobKey: string,
  feedbackType: FeedbackType = FeedbackType.SAVED,
): Promise<void> {
  try {
    const jobsWithFeedback = await getJobsWithFeedback();

    // Add to the object if not already present
    if (!jobsWithFeedback[jobKey]) {
      jobsWithFeedback[jobKey] = {
        type: feedbackType,
      };
      await saveJobsWithFeedback(jobsWithFeedback);
    }
  } catch (error) {
    console.error("Failed to mark job with feedback:", error);
  }
}

/**
 * Check if a job has received feedback
 * @param jobKey Job ID
 * @returns Returns true if the job has received feedback, false otherwise
 */
export async function hasJobFeedback(jobKey: string): Promise<boolean> {
  try {
    const jobsWithFeedback = await getJobsWithFeedback();
    return !!jobsWithFeedback[jobKey];
  } catch (error) {
    console.error("Failed to check if job has feedback:", error);
    return false;
  }
}
